import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil } from "lucide-react";

export default function ApplicationDetail() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [sched, setSched] = useState(null);
  useEffect(() => {
    api.get(`/applications/${id}`).then((r) => setA(r.data));
    api.get(`/applications/${id}/schedule`).then((r) => setSched(r.data));
  }, [id]);

  if (!a) return <Layout><div className="text-slate-500">Loading…</div></Layout>;

  const editable = ["draft", "awaiting_ekyc", "awaiting_documents", "more_info_requested"].includes(a.status);

  return (
    <Layout>
      {["approved", "disbursement_pending"].includes(a?.status) && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm">
            <div className="font-semibold text-blue-900">
              {a?.agreement_signed ? "Your loan agreement is signed" : "Your loan is approved — sign the agreement"}
            </div>
            <div className="text-blue-800">
              {a?.agreement_signed
                ? "A copy of the signed agreement is available to download."
                : "Funds are released once you have read and signed the agreement."}
            </div>
          </div>
          <Link to={`/borrower/applications/${id}/agreement`}>
            <Button className="bg-blue-900 hover:bg-blue-800" data-testid="go-sign-agreement">
              {a?.agreement_signed ? "View agreement" : "Review and sign"}
            </Button>
          </Link>
        </div>
      )}
      <div className="fade-in space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Application</div>
            <h1 className="text-2xl font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>{a.product_name}</h1>
            <div className="text-sm text-slate-600 mt-1">${a.amount.toFixed(2)} · {a.duration} {a.duration_unit}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={a.status}/>
            {editable && <Link to={`/borrower/applications/${a.id}/edit`}><Button variant="outline" size="sm" data-testid="edit-application-btn"><Pencil className="h-4 w-4 mr-1"/> Edit</Button></Link>}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="rounded-xl border-slate-200"><CardContent className="p-6 space-y-2">
            <h3 className="font-semibold text-slate-900" style={{fontFamily:'Outfit'}}>Calculator snapshot</h3>
            {a.calculator_snapshot && (
              <div className="text-sm space-y-1">
                <Row label="Principal" value={`$${a.calculator_snapshot.principal.toFixed(2)}`}/>
                <Row label="Interest" value={`$${a.calculator_snapshot.interest.toFixed(2)}`}/>
                <Row label="Total fees" value={`$${a.calculator_snapshot.total_fees.toFixed(2)}`}/>
                <Row label="Total payable" value={`$${a.calculator_snapshot.total_payable.toFixed(2)}`} bold/>
                <Row label="Effective APR" value={`${a.calculator_snapshot.effective_apr}%`} accent/>
                <Row label="Per payment" value={`$${a.calculator_snapshot.per_payment.toFixed(2)} × ${a.calculator_snapshot.n_payments}`}/>
              </div>
            )}
          </CardContent></Card>

          <Card className="rounded-xl border-slate-200"><CardContent className="p-6 space-y-2">
            <h3 className="font-semibold text-slate-900" style={{fontFamily:'Outfit'}}>Status checklist</h3>
            <div className="text-sm space-y-1">
              <Row label="eKYC" value={a.ekyc?.status || "not started"}/>
              <Row label="Documents" value={`${(a.documents||[]).length} uploaded`}/>
              <Row label="Bank" value={a.bank_nomination ? a.bank_nomination.bank_name : "—"}/>
              <Row label="Submitted at" value={a.submitted_at ? new Date(a.submitted_at).toLocaleString() : "—"}/>
              <Row label="Decision" value={a.decision_at ? `${new Date(a.decision_at).toLocaleString()}` : "Pending"}/>
              {a.decision_note && <div className="text-xs text-slate-500 mt-2 italic">"{a.decision_note}"</div>}
            </div>
          </CardContent></Card>
        </div>

        <Card className="rounded-xl border-slate-200"><CardContent className="p-6">
          <h3 className="font-semibold text-slate-900 mb-3" style={{fontFamily:'Outfit'}}>Documents</h3>
          {(a.documents||[]).length === 0 ? <p className="text-sm text-slate-500">No documents yet.</p> :
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
              {a.documents.map((d) => (
                <li key={d.id} className="px-4 py-3 flex items-center justify-between" data-testid={`detail-doc-${d.id}`}>
                  <div>
                    <div className="font-medium text-slate-900">{d.doc_type}</div>
                    <div className="text-xs text-slate-500">{d.filename}</div>
                    {d.comment && <div className="text-xs text-amber-700 mt-0.5">Reviewer note: {d.comment}</div>}
                  </div>
                  <StatusBadge status={d.status}/>
                </li>
              ))}
            </ul>
          }
        </CardContent></Card>

        {sched?.schedule && (
          <Card className="rounded-xl border-slate-200"><CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-3" style={{fontFamily:'Outfit'}}>Repayment schedule</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">
                    <th className="py-2">#</th><th>Due date</th><th>Amount</th><th>Principal</th><th>Interest</th>
                  </tr>
                </thead>
                <tbody>
                  {sched.schedule.map((s) => (
                    <tr key={s.installment} className="border-b last:border-b-0 border-slate-100">
                      <td className="py-2">{s.installment}</td>
                      <td>{s.due_date}</td>
                      <td className="font-medium">${s.amount.toFixed(2)}</td>
                      <td>${s.principal_portion.toFixed(2)}</td>
                      <td>${s.interest_portion.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        )}

        {(a.notes||[]).length > 0 && (
          <Card className="rounded-xl border-slate-200"><CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-3" style={{fontFamily:'Outfit'}}>Reviewer notes</h3>
            <ul className="space-y-2">
              {a.notes.map((n) => (
                <li key={n.id} className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-sm">
                  <div className="text-xs text-slate-500">{n.reviewer_email} · {n.action} · {new Date(n.created_at).toLocaleString()}</div>
                  <div className="text-slate-800">{n.note}</div>
                </li>
              ))}
            </ul>
          </CardContent></Card>
        )}
      </div>
    </Layout>
  );
}

function Row({label, value, bold, accent}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500 capitalize">{label}</span>
      <span className={`${bold ? "font-semibold" : ""} ${accent ? "text-emerald-700" : "text-slate-900"} capitalize`}>{String(value).replaceAll('_',' ')}</span>
    </div>
  );
}
